import React, { useState, useEffect, useRef } from 'react';
import importAllImg from "../helpers/import-all-imgs";
import {useIntl} from "react-intl";
import Partnerships from "./Partnerships";

const imgs = importAllImg(require.context('../img/npf', false, /\.(png|jpe?g|svg)$/));


function ChangeMakers() {

  const { formatMessage, locale } = useIntl();
  const fm = id => formatMessage({id});

  const resources = [
    {
      authorKey: 'By Ashoka',
      titleKey: 'An Introduction to Systems Change',
      url: 'https://www.ashoka.org/en-us/story/introduction-systems-change'
    },
    {
      authorKey: 'By CATALYST 2030',
      titleKey: 'About Systems Change',
      url: 'https://catalyst2030.net/what-is-systems-change/'
    },
    {
      authorKey: 'By FSG',
      titleKey: 'Systems Change is a Noun and a Verb',
      url: 'https://www.fsg.org/blog/systems-change-noun-and-verb'
    },
    {
      authorKey: 'By Skoll Foundation',
      titleKey: 'Centered Self: Systems Change From The Inside Out',
      url: 'https://skoll.org/2019/04/09/centered-self-systems-change-from-the-inside-out/'
    }
  ];

  const partnerships = [
    {
      titleKey: 'ASHOKA, GLOBAL',
      descriptionKeys: [
        'Ashoka, founded in 1980, identifies and supports the world’s leading social entrepreneurs, learns from the patterns in their innovations, and mobilizes a global community that embraces these new frameworks to build an “Everyone a Changemaker World.”',
        'The Nguyen-Phuong Family Endowment, established in 2014, is dedicated to supporting social entrepreneurs who operate in emerging markets – a permanent symbol of the family’s keen commitment to social services in the developing world.'
      ],
      logo: 'image 2297.png',
      status: 'ongoing',
      url: 'https://www.ashoka.org/en-us/ashokas-endowments'
    },
    {
      titleKey: 'ACUMEN, GLOBAL',
      descriptionKeys: [
        'Acumen’s mission, founded in 2001, is to change the way the world tackles poverty by using the power of entrepreneurship to build a world where everyone has the opportunity to live with dignity. Acumen invests patient, long-term capital in innovative and sustainable startups that deliver critical goods and services to the world’s poor.',
        'Our family is supporting Acumen to grow and accompany the community of Changemakers across South East Asia. We are also supporting initiatives in Vietnam through accelerators, the academy and fellowship.'
      ],
      logo: 'image 2298.png',
      status: 'ongoing',
      url: 'https://acumen.org/'
    },
    {
      titleKey: 'ENDEAVOR, Global',
      descriptionKeys: [
        'Endeavor (founded in 1997) is the world’s leading community of high-impact entrepreneurs. Its mission is to unlock the transformational power of entrepreneurship by selecting, supporting, and investing in the world’s top founders. Endeavor’s unique entrepreneur-first model and network of trust provide a platform for founders to dream big, scale up, and pay it forward to the next generation of entrepreneurs.',
        'Our family is supporting Endeavor with fellowship growth and providing advice to individual fellows.'
      ],
      logo: 'image 2299.png',
      status: 'ongoing',
      url: 'https://endeavor.org/network'
    },
  ];

  return (
    <>
      <div className='mt-14 md:mt-24 relative'>
        <img className='hidden md:block w-full' src={imgs['Group 3019.png']}/>
        <img className='md:hidden block w-full' src={imgs['Group 3020.png']}/>

        <div
          className='w-[227px] md:w-[600px] text-center absolute text-lg md:text-5xl font-bold text-white z-1 md:leading-[1.2] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
          {
            locale === 'en' ? 'Powering platforms for Changemakers to build new systems' :
              'Tiếp sức cho các nền tảng dành cho Nhà Kiến Tạo Thay Đổi nhằm xây dựng những hệ thống mới'
          }
        </div>
      </div>

      <div className='bg-white py-4 md:py-10 px-5 md:px-[7.5%]'>
        <div className='text-center mb-2 md:mb-3'>
          <h2 className='text-sm md:text-xl font-bold uppercase'>
            {fm('Platform for Changemakers')}
          </h2>
        </div>
        <div className='max-w-[90%] md:max-w-2xl mx-auto'>
          <p className='text-base text-black md:text-2xl leading-relaxed text-center'>
            {fm('We support social entrepreneurs, Changemakers and system builders addressing the root causes of inequality through innovative, locally grounded, and values-driven solutions.')}
          </p>
        </div>
      </div>

      <div className='block md:hidden'>
        <img src={imgs['hermes-rivera-R1_ibA4oXiI-unsplash 4.png']} className='w-full'/>
      </div>
      <div className='bg-white py-2 md:py-20 px-8 md:px-[7.5%]'>
        <h2 className='text-lg md:text-3xl font-bold mb-2 md:mb-4 text-black'>
          {fm('Why Support Changemakers for Systems Change?')}
        </h2>
        <div className='flex flex-col md:flex-row gap-8 md:gap-24 items-start'>
          {/* Text Section - Left */}
          <div className='flex-1 md:flex-[2]'>

            <div className='space-y-4 md:space-y-6 text-sm md:text-lg leading-relaxed text-black'>
              <p>
                {fm('Systems change is about addressing the root causes of social problems, which are often intractable and embedded in networks of cause and effect. It is an intentional process designed to fundamentally alter the components and structures that cause the system to behave in a certain way.')}
              </p>
              <p>
                {
                  locale === 'en' ? <>
                    Following organizations like Ashoka and Acumen, we believe that the most powerful force for good is
                    a systems-changing idea in the hands of a formidable social entrepreneur/changemaker. True systems
                    change is the result of <b>scaling social impact</b> rather than focusing on growing a single
                    organisation; it is the result of <b>letting go of control of an idea</b>, and enabling and
                    encouraging others to join in and implement change.
                  </> : <>
                    Theo định hướng của các tổ chức như Ashoka và Acumen, chúng tôi tin rằng sức mạnh lớn nhất cho điều
                    thiện chính là một ý tưởng có khả năng thay đổi hệ thống khi được trao vào tay một doanh nhân xã hội
                    / nhà kiến tạo thay đổi đủ bản lĩnh. Thay đổi hệ thống thực sự không đến từ việc mở rộng quy mô của
                    một tổ chức đơn lẻ, mà từ việc nhân rộng tác động xã hội; không phải bằng cách nắm giữ quyền kiểm
                    soát một ý tưởng, mà bằng việc buông bỏ, trao quyền và khuyến khích nhiều người cùng tham gia hiện
                    thực hóa sự thay đổi đó.
                  </>
                }
              </p>

            </div>
          </div>
          {/* Image Section - Right */}
          <div className='flex-1 md:flex-[1] w-full md:w-auto hidden md:block'>
            <img
              src={imgs['hermes-rivera-R1_ibA4oXiI-unsplash 2.png']}
              alt="Who We Are"
              className='w-full h-auto object-cover'
            />
          </div>
        </div>
      </div>

      {/*Our Vision*/}
      <div className='bg-white py-2 md:py-8 px-8 md:px-[7.5%]'>
        <h2 className='text-lg md:text-3xl font-bold mb-2 md:mb-4 text-black'>
          {fm('Our Vision')}
        </h2>
        <p className='text-sm md:text-lg leading-relaxed text-black mb-6 md:mb-8'>
          {fm('Our family supports organizations, social entrepreneurs and changemakers using a Systems Change mindset to address our world\'s most pressing issues. Here are some resources to learn more about Systems Change:')}
        </p>

        {/* Desktop Grid - 4 cards in a row */}
        <div className='hidden md:grid md:grid-cols-4 gap-4 md:gap-10'>
          {resources.map((resource, index) => (
            <div
              key={index}
              onClick={() => resource.url && window.open(resource.url, '_blank')}
              className='bg-[#F3F7E6] p-6 flex flex-col cursor-pointer hover:opacity-90 transition-opacity'
            >
              <div className='text-lg text-[#354402] mb-2 font-bold'>
                {fm(resource.authorKey)}
              </div>
              <div className='text-2xl font-semibold text-black flex-1 mb-4'>
                {fm(resource.titleKey)}
              </div>
              <div className='flex justify-end'>
                <img
                  src={imgs['arrow-down-right-svgrepo-com 14.svg']}
                  alt="Learn more"
                  className='w-6 h-6'
                />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile List - Stacked vertically */}
        <div className='block md:hidden space-y-3'>
          {resources.map((resource, index) => (
            <div
              key={index}
              onClick={() => resource.url && window.open(resource.url, '_blank')}
              className='min-h-[76px] border-l-[10px] border-l-[#354402] bg-[#F3F7E6] flex items-center cursor-pointer hover:opacity-90 transition-opacity'
            >
              {/* Dark green vertical bar on left */}
              <div
                className='w-1 h-full flex-shrink-0 bg-[#354402]'
              ></div>

              {/* Content */}
              <div className='flex-1 px-4 py-2'>
                <div className='text-xs text-[#354402] font-bold mb-1'>
                  {fm(resource.authorKey)}
                </div>
                <div className='text-sm font-semibold text-black'>
                  {fm(resource.titleKey)}
                </div>
              </div>

              {/* Arrow Icon */}
              <div className='pr-4 flex-shrink-0'>
                <img
                  src={imgs['arrow-down-right-svgrepo-com 14.svg']}
                  alt="Learn more"
                  className='w-5 h-5'
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/*Our Approach*/}
      <div className='bg-white py-2 md:py-8 px-8 md:px-[7.5%]'>
        <h2 className='text-lg md:text-3xl font-bold mb-2 md:mb-4 text-black'>
          {fm('Our Approach')}
        </h2>

        <div className='space-y-4 md:space-y-3 text-sm md:text-lg leading-relaxed text-black'>
          <h2>
            We are living in a time of <b>unprecedented global challenges.</b>
          </h2>
          <p>
            {fm('Our Vision is to develop a hub for social impact and philanthropy in Asia. We will want this center to support a new generation of leaders and changemakers to address the regions most critical challenges. ')}
          </p>

          <p>
            {fm('We believe in working with organizations and leaders on the ground to help scale solutions and build collaboratives on topical areas. This platform will cover the following areas: research & knowledge, convening & collaboration, leadership development and developing a patient capital fund.')}
          </p>
        </div>
      </div>

      {/*Partnerships*/}
      <Partnerships partnerships={partnerships} />

      {/* bottom part - Quote section */}
      <div className='relative -mb-1 md:mb-auto md:py-20 px-5 md:px-10 h-[150px] md:h-[400px] flex items-center justify-end'>
        <img
          src={imgs['Group 30091.png']}
          alt="Quote Background"
          className='absolute inset-0 w-full h-[150px] md:h-auto'
        />
        <div className='relative mt-0 z-10 max-w-[205px] md:max-w-2xl mx-auto'>
          <div className='text-xs md:text-2xl font-bold text-black leading-[1.3] md:leading-relaxed text-center'>
            {
              locale === 'en' ?
                <>
                  Social entrepreneurs are not content just to give a fish or teach how to fish. They will not rest until they have revolutionised the fishing industry.
                </> :
                <>
                  Các nhà khởi nghiệp xã hội không chỉ bằng lòng với việc cho cá hoặc dạy cách câu cá. Họ sẽ không ngừng nghỉ cho đến khi tạo ra cuộc cách mạng trong ngành công nghiệp đánh bắt cá.
                </>
            }
            <p className='mt-5'>{fm('Bill Drayton')}</p>
          </div>
        </div>
      </div>

    </>
  )
}

export default ChangeMakers;
